import { toTypedSchema } from '@vee-validate/zod';
import {
  type CountryCode,
  getExampleNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js/min';
import examples from 'libphonenumber-js/mobile/examples';
import { computed } from 'vue';
import { z } from 'zod';
import translation from 'zod-i18n-map/locales/nl/zod.json';

export function usePhoneNumberValidator({
  country = 'NL' as CountryCode,
  isRequired = false,
  maxLength = 0,
}) {
  function getErrorMessageByType(type: string) {
    switch (type) {
      case 'NOT_A_NUMBER':
        return 'Vul een telefoonnummer met cijfers in';
      case 'INVALID_COUNTRY':
        return 'Vul een bestaande landcode in';
      case 'TOO_SHORT':
        return 'Vul een langer telefoonnummer in';
      default:
        return 'Vul een geldig telefoonnummer in';
    }
  }

  function validate(value: string) {
    try {
      parsePhoneNumberWithError(value, country);
    } catch (error) {
      throw new Error(getErrorMessageByType(error instanceof Error ? error.message : ''));
    }
  }

  function superRefineFormat(value: string, ctx: z.RefinementCtx) {
    if (!value.length) {
      return;
    }

    try {
      validate(value);
    } catch (error) {
      if (!(error instanceof Error)) {
        return;
      }

      const example = getExampleNumber(country, examples)?.number;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${error.message} (bijvoorbeeld ${example})`,
      });
    }
  }

  const schema = computed(() => {
    const validation = z
      .string({ invalid_type_error: translation.errors.invalid_type_received_undefined })
      .transform((val) => val?.replace(/\s/g, ''))
      .pipe(
        z
          .string()
          .max(maxLength)
          .superRefine(superRefineFormat)
          .refine((val) => val.length, translation.errors.invalid_type_received_undefined),
      );

    return isRequired ? validation : validation.nullable().optional().or(z.literal(''));
  });

  const typedSchema = computed(() => toTypedSchema(schema.value));

  return {
    typedSchema,
    schema,
    validate,
  };
}
